// Post-publish resolution front-ends, including the credible-exit escape hatches (szzt-spec.md
// §11.3, §11.4, §11.5, §9.7).
//
// After a publish, the front-end surfaces where a site can be reached. §11.4 enumerates four
// front-ends of decreasing mutability; this module derives their URLs from a `PublishResult` so the
// UI can show not just the convenient slug but also the gateway-independent routes (the derived
// per-site origin, the raw `web+rasl` content address, the version-pinned origin) that make credible
// exit exercised rather than merely specified (§11.3). Pure string derivation — no network, unit-testable.

import { siteOriginLabel } from "@szzt/core";
import { parseAtUri } from "@szzt/records";
import type { PublishResult } from "@szzt/publish";

/** Inputs for {@link exitRoutes}: the publish result plus the gateway apex and optional naming. */
export interface ExitRoutesInput {
  /** The §11.4/§11.5 fields of a publish result (root CID, site rkey, and the record AT-URIs). */
  result: Pick<PublishResult, "rootCid" | "siteRkey" | "tileUri" | "deploymentUri" | "siteUri">;
  /** Gateway apex (PSL-listing recommended, not required — §9.3), e.g. `mini.site`. Used for the host-based routes. */
  apex: string;
  /** The gateway-allocated slug, if one was granted (§11.1). Omitted → no `slug` route. */
  slug?: string;
  /** Site-level mirror hints for the `web+rasl` address (§11.4, §13.1). */
  hints?: string[];
}

/**
 * The resolution front-ends for a published site (§11.4), from most gateway-governed to purely
 * content-addressed. `siteOrigin`, `rasl`, and `versionOrigin` are the credible-exit routes a
 * security-sensitive consumer SHOULD prefer over the slug (§11.3).
 *
 * There is deliberately no owner/identity input: the derived origin is DID-addressed (§11.3), the DID is
 * the site's owner parsed from `siteUri`, and a handle cannot occupy a single DNS label — so a handle can
 * never produce this URL, and the type offers no way to pass one.
 */
export interface ExitRoutes {
  /** `https://<slug>.<apex>/` — gateway slug index; mutable, gateway-governed (§11.1). Absent if no slug. */
  slug?: string;
  /**
   * `https://<siteRkey>--<did-suffix>.<apex>/` — the derived per-site origin (§11.3): DID-addressed,
   * signed pointer, computed with no index. `null` — never a broken URL — when the owner is a `did:web`
   * (its dots can't live in one DNS label) or the record key can't round-trip a case-insensitive DNS
   * label; such a site is reachable only by slug (§11.1) or custom domain (§11.2), not by identity.
   */
  siteOrigin: string | null;
  /** `web+rasl://<rootCID>;<hints>/` — direct content address; immutable, hash-only (§11.4). */
  rasl: string;
  /** `https://<rootCID>.<apex>/` — version-pinned origin for this exact build; immutable (§11.5). */
  versionOrigin: string;
  /** `at://<did>/app.szzt.deployment/<rkey>` — immutable version permalink (§11.5). */
  deploymentUri: string;
  /** `at://<did>/ing.dasl.masl/<rkey>` — the tile record; loads in a stock Web Tiles mothership (§9.7). */
  tileUri: string;
}

/** Derive the §11.4 resolution front-ends for a published site. */
export function exitRoutes(input: ExitRoutesInput): ExitRoutes {
  const { result, apex } = input;
  const owner = parseAtUri(result.siteUri).did;
  // The derived per-site origin is a pure function of (owner DID, siteRkey) — no handle, no lookup (§11.3).
  // `siteOriginLabel` returns null for a did:web owner or a non-round-trippable rkey, and we propagate that
  // null rather than emit a URL that would resolve nowhere.
  const label = siteOriginLabel(owner, result.siteRkey);

  const routes: ExitRoutes = {
    siteOrigin: label !== null ? `https://${label}.${apex}/` : null,
    rasl: raslUrl(result.rootCid, input.hints),
    versionOrigin: `https://${result.rootCid}.${apex}/`,
    deploymentUri: result.deploymentUri,
    tileUri: result.tileUri,
  };
  if (input.slug !== undefined) routes.slug = `https://${input.slug}.${apex}/`;
  return routes;
}

/** Build the `web+rasl://<rootCID>;<hints>/` content address (§11.4). Hints are comma-joined; omitted when empty. */
export function raslUrl(rootCid: string, hints?: string[]): string {
  const list = hints?.filter((h) => h.length > 0) ?? [];
  const suffix = list.length > 0 ? `;${list.join(",")}` : "";
  return `web+rasl://${rootCid}${suffix}/`;
}

/**
 * Advisory client-side slug validation (§11.1): lowercase, a-z/0-9 and single interior hyphens only,
 * 3–63 chars (DNS label bound), no leading/trailing/double hyphen. This gates the front-end's slug
 * field early; the gateway's allocation policy (reserved names, confusable/homograph rules) remains
 * authoritative and is NOT re-implemented here.
 */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{1,61})[a-z0-9]$/.test(slug) && !slug.includes("--");
}
